import React, { useState, useEffect } from 'react'; 
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { FaCoins, FaDollarSign, FaCheckCircle, FaTimesCircle, FaLink, FaSearch } from 'react-icons/fa';
import AdsComponent from './AdsComponent';
import './WalletSearch.css';

const WalletSearch = () => {
  const { address: paramAddress } = useParams();
  const [address, setAddress] = useState(paramAddress || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (paramAddress) {
      handleSearch();
    }
  }, [paramAddress]);

  const handleSearch = async () => {
    if (!address) {
      setError('Please enter a wallet address.');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      const response = await axios.get(`/api/pool-search-wallet/${address}`);
      
      if (response.data) {
        setResults(response.data);
      } else {
        setError('No results were found for this address.');
      }
    } catch (err) {
      console.error('Error searching for wallet:', err);
      setError('Error searching for wallet.');
    } finally {
      setLoading(false);
    }
  };

  const getCoinImage = (symbol) => {
    const sanitizedSymbol = symbol.split(/[-_]/)[0].toUpperCase();
    return `/img/coins/${sanitizedSymbol}.webp`;
  };

  const getPoolImage = (poolName) => {
    return `/img/pools/${poolName.toLowerCase()}.webp`;
  };

  return (
    <div className="wallet-search-component">
      <h1>Wallet Address Search</h1>

      <p className="info-text">
        This search tool attempts to look for the given wallet address across multiple mining pools.
        However, the success of the search may depend on the API limitations of each pool.
      </p>
      <div className="sidebar-ad">
          <AdsComponent requiredSize="728x90" />
      </div>     

      <div className="search-form">
        <input
          type="text"
          className="search-input"
          placeholder="Enter wallet address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        <button onClick={handleSearch} className="search-button" disabled={loading}>
          {loading ? 'Searching...' : <FaSearch />}
        </button>
      </div>

      {error && <p className="error-text">{error}</p>}

      {results.length > 0 && (
        <div className="results">
          <h2>Results</h2>
          {results.map((result, index) => (
            <div key={index} className="pool-result">
              <div className="pool-card">
                <h3>{result.pool_name}</h3>

                <img
                  src={getPoolImage(result.pool_name)}
                  alt={result.pool_name}
                  onError={(e) => { e.target.src = '/img/pools/default.webp'; }}
                  className="pool-image"
                />

                <p>
                  <FaLink />
                  <strong> Pool URL: </strong> 
                  <a href={result.url_pool} target="_blank" rel="noopener noreferrer">{result.url_pool}</a>
                </p>
              </div>

              <div className="currency-info">
                <img
                  src={getCoinImage(result.data.currency)}
                  alt={result.data.currency}
                  onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                  className="currency-image"
                />
                <div className="currency-card">
                  <p><FaCoins /> <strong>Currency:</strong> {result.data.currency}</p>
                </div>
                <div className="currency-card">
                  <p><FaDollarSign /> <strong>Balance:</strong> {result.data.balance}</p>
                </div>
                <div className="currency-card">
                  <p><FaDollarSign /> <strong>Unpaid:</strong> {result.data.unpaid}</p>
                </div>
                <div className="currency-card">
                  <p><FaDollarSign /> <strong>Total:</strong> {result.data.total}</p>
                </div>
                <div className="currency-card">
                  <p><FaDollarSign /> <strong>Unsold:</strong> {result.data.unsold}</p>
                </div>
              </div>

              <h4>Miners</h4>
              <div className="miners-table-container">
                <table className="miners-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Algorithm</th>
                      <th>Difficulty</th>
                      <th>Accepted</th>
                      <th>Rejected</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.miners.map((miner, i) => (
                      <tr key={i}>
                        <td>{miner.version}</td>
                        <td>{miner.algo}</td>
                        <td>{miner.difficulty}</td>
                        <td><FaCheckCircle style={{ color: 'green' }} /> {miner.accepted}</td>
                        <td><FaTimesCircle style={{ color: 'red' }} /> {miner.rejected}</td>
                        <td>{miner.ID}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WalletSearch;
